import { Menu } from "@mantine/core";
import { ChevronDown } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

export type TabDefinition = {
  to: string;
  label: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  count?: number;
  /** If set, this tab becomes a dropdown with sub-items instead of a direct link */
  children?: { to: string; label: string; icon?: React.ComponentType<React.SVGProps<SVGSVGElement>> }[];
};

type OverflowTabBarProps = {
  tabs: TabDefinition[];
  className?: string;
};

const OverflowTabBar = ({ tabs, className = "" }: OverflowTabBarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const location = useLocation();

  const measureTabs = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const moreButtonWidth = 100;
    const containerWidth = container.offsetWidth;
    let usedWidth = 0;
    let count = 0;

    for (let i = 0; i < tabs.length; i++) {
      const el = tabRefs.current.get(i);
      const tabWidth = el ? el.offsetWidth : 90;
      if (usedWidth + tabWidth > containerWidth - (i < tabs.length - 1 ? moreButtonWidth : 0)) {
        break;
      }
      usedWidth += tabWidth;
      count++;
    }

    setVisibleCount(count || 1);
  }, [tabs.length]);

  useEffect(() => {
    measureTabs();
    const observer = new ResizeObserver(() => measureTabs());
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, [measureTabs]);

  useEffect(() => {
    measureTabs();
  }, [tabs, measureTabs]);

  const visibleTabs = tabs.slice(0, visibleCount);
  const overflowTabs = tabs.slice(visibleCount);

  const baseClass = "flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors rounded-none";
  const inactiveClass = `${baseClass} border-transparent text-gray-500 hover:text-gray-800`;
  const activeClass = `${baseClass} border-purple-600 text-gray-900 bg-transparent shadow-none`;

  /** Check if any child route of a dropdown tab is active */
  const isDropdownActive = (tab: TabDefinition) => {
    if (!tab.children) return false;
    return tab.children.some((child) => location.pathname.endsWith(`/${child.to}`));
  };

  return (
    <nav
      ref={containerRef}
      className={`flex items-center bg-transparent h-auto p-0 gap-0 ${className}`}
    >
      {visibleTabs.map((tab, index) => (
        <div
          key={tab.to}
          ref={(el) => {
            if (el) tabRefs.current.set(index, el);
          }}
        >
          {tab.children ? (
            /* Dropdown tab */
            <Menu shadow="md" width={180} position="bottom-start">
              <Menu.Target>
                <button
                  type="button"
                  className={isDropdownActive(tab) ? activeClass : inactiveClass}
                >
                  {tab.icon && <tab.icon className="h-3.5 w-3.5 flex-shrink-0" />}
                  {tab.label}
                  <ChevronDown className="h-3 w-3 ml-0.5" />
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                {tab.children.map((child) => (
                  <Menu.Item key={child.to} component={NavLink} to={child.to}>
                    <div className="flex items-center gap-1.5 text-xs">
                      {child.icon && <child.icon className="h-3.5 w-3.5 flex-shrink-0" />}
                      {child.label}
                    </div>
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ) : (
            /* Regular tab link */
            <NavLink to={tab.to}>
              {({ isActive }) => (
                <div className={isActive ? activeClass : inactiveClass}>
                  {tab.icon && <tab.icon className="h-3.5 w-3.5 flex-shrink-0" />}
                  {tab.label}
                </div>
              )}
            </NavLink>
          )}
        </div>
      ))}

      {overflowTabs.length > 0 && (
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <button
              type="button"
              className={`${baseClass} border-transparent text-gray-500 hover:text-gray-700`}
            >
              +{overflowTabs.length} more
              <ChevronDown className="h-3 w-3" />
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            {overflowTabs.map((tab) =>
              tab.children ? (
                tab.children.map((child) => (
                  <Menu.Item key={child.to} component={NavLink} to={child.to}>
                    <div className="flex items-center gap-1.5 text-xs">
                      {child.icon && <child.icon className="h-3.5 w-3.5 flex-shrink-0" />}
                      {child.label}
                    </div>
                  </Menu.Item>
                ))
              ) : (
                <Menu.Item key={tab.to} component={NavLink} to={tab.to}>
                  <div className="flex items-center gap-1.5 text-xs">
                    {tab.icon && <tab.icon className="h-3.5 w-3.5" />}
                    {tab.label}
                  </div>
                </Menu.Item>
              )
            )}
          </Menu.Dropdown>
        </Menu>
      )}
    </nav>
  );
};

export default OverflowTabBar;
